/** Details!-style meters computed from the event buffer. */
import type { Meters, Ms, PullSegment, SimEvent, UnitId, UnitMeters } from '../contracts';
import { MELEE_ABILITY_ID } from '../contracts';
import { ownerOf, type UnitMeta } from './meta';

function emptyMeters(id: UnitId, meta: UnitMeta | undefined, activeMs: Ms): UnitMeters {
  return {
    unitId: id,
    name: meta?.name ?? id,
    role: meta?.role ?? null,
    classId: meta?.classId ?? null,
    damageDone: 0,
    healingDone: 0,
    overhealing: 0,
    damageTaken: 0,
    meleeTaken: 0,
    aoeTaken: 0,
    selfHealing: 0,
    absorbsDone: 0,
    interrupts: 0,
    dispels: 0,
    deaths: 0,
    casts: 0,
    castsByAbility: {},
    damageByAbility: {},
    activeMs,
  };
}

/** Time inside pull segments clipped to [fromMs, toMs]; the whole window when there are no pulls. */
export function combatTimeIn(pulls: readonly PullSegment[], fromMs: Ms, toMs: Ms): Ms {
  if (pulls.length === 0) return Math.max(0, toMs - fromMs);
  let total = 0;
  for (const p of pulls) {
    const s = Math.max(fromMs, p.startMs);
    const e = Math.min(toMs, p.endMs ?? toMs);
    if (e > s) total += e - s;
  }
  return total;
}

export function computeMeters(
  events: readonly SimEvent[],
  meta: ReadonlyMap<UnitId, UnitMeta>,
  pulls: readonly PullSegment[],
  window?: { fromMs?: Ms; toMs?: Ms },
): Meters {
  const fromMs = window?.fromMs ?? 0;
  const toMs = window?.toMs ?? events.at(-1)?.timestamp ?? 0;
  const activeMs = combatTimeIn(pulls, fromMs, toMs);
  const units: Record<UnitId, UnitMeters> = {};
  const get = (id: UnitId): UnitMeters => (units[id] ??= emptyMeters(id, meta.get(id), activeMs));

  for (const e of events) {
    if (e.timestamp < fromMs || e.timestamp > toMs) continue;
    switch (e.type) {
      case 'damage': {
        const total = e.amount + e.absorbed;
        const src = ownerOf(meta, e.sourceID);
        if (src !== e.targetID) {
          const s = get(src);
          s.damageDone += total;
          s.damageByAbility[e.abilityGameID] = (s.damageByAbility[e.abilityGameID] ?? 0) + total;
        }
        const t = get(e.targetID);
        t.damageTaken += total;
        if (e.abilityGameID === MELEE_ABILITY_ID) t.meleeTaken += total;
        if (e.isAoE) t.aoeTaken += total;
        break;
      }
      case 'heal': {
        const s = get(ownerOf(meta, e.sourceID));
        s.healingDone += e.amount;
        s.overhealing += e.overheal;
        if (e.sourceID === e.targetID) s.selfHealing += e.amount;
        break;
      }
      case 'absorbed':
        get(ownerOf(meta, e.sourceID)).absorbsDone += e.amount;
        break;
      case 'cast': {
        const s = get(e.sourceID);
        s.casts += 1;
        s.castsByAbility[e.abilityGameID] = (s.castsByAbility[e.abilityGameID] ?? 0) + 1;
        break;
      }
      case 'interrupt':
        get(ownerOf(meta, e.sourceID)).interrupts += 1;
        break;
      case 'dispel':
        get(ownerOf(meta, e.sourceID)).dispels += 1;
        break;
      case 'death':
        get(e.targetID).deaths += 1;
        break;
      default:
        break;
    }
  }
  return { fromMs, toMs, units };
}

/** Damage per second over in-combat time. */
export function dps(m: UnitMeters): number {
  return m.activeMs > 0 ? (m.damageDone * 1000) / m.activeMs : 0;
}

/** Healing (incl. absorbs) per second over in-combat time. */
export function hps(m: UnitMeters): number {
  return m.activeMs > 0 ? ((m.healingDone + m.absorbsDone) * 1000) / m.activeMs : 0;
}

/** Damage taken per second over in-combat time. */
export function dtps(m: UnitMeters): number {
  return m.activeMs > 0 ? (m.damageTaken * 1000) / m.activeMs : 0;
}
